import React, { useState } from 'react';

import { verticalRulerWidthPx } from 'src/editor';
import { makeStyles } from 'tss-react/mui';

import { SuggestionsAlertActionButtons } from './SuggestionsAlertActionButtons';
import { SuggestionsAlertNav } from './SuggestionsAlertNav';
import { SuggestionsAlertStatus } from './SuggestionsAlertStatus';
import type { EditorColumnBounds } from './use-editor-column-bounds';
import { useEditorColumnBounds } from './use-editor-column-bounds';
import { useAutoScrollToFirstAISuggestion } from './useAutoScrollToFirstAISuggestion';
import { editorSuggestionsAlertId } from '../editor-alert-ids';

const useStyles = makeStyles()(theme => ({
  actionSection: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  container: {
    backgroundColor: '#FFF8F0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    left: getLeftInsetPx(),
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: theme.zIndex.appBar,
  },
  content: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 1, 2),
  },
  statusSection: {
    flex: 1,
    minWidth: 0,
  },
}));

export function SuggestionsAlert(): JSX.Element | null {
  const { classes } = useStyles();
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const { scrollLeftPx, visibleWidthPx } = useEditorColumnBounds(containerEl);

  useAutoScrollToFirstAISuggestion();

  return (
    <div
      ref={setContainerEl}
      className={classes.container}
      id={editorSuggestionsAlertId}
      style={getPinnedToColumnStyle({ scrollLeftPx, visibleWidthPx })}
    >
      <div className={classes.content}>
        <div className={classes.statusSection}>
          <SuggestionsAlertStatus />
        </div>
        <div className={classes.actionSection}>
          <SuggestionsAlertNav />
          <SuggestionsAlertActionButtons />
        </div>
      </div>
    </div>
  );
}

/**
 * The banner is positioned against the document, which keeps a minimum width and so is wider
 * than the column once the right panel takes enough of it. Left alone, the banner would grow
 * with the document and run off screen under the panel and the left nav. Offsetting it by the
 * document's scroll position and sizing it to the visible column instead keeps it spanning
 * exactly what the reader can see, whatever the panel's width.
 */
function getPinnedToColumnStyle({ scrollLeftPx, visibleWidthPx }: EditorColumnBounds) {
  // The column has not been measured yet (e.g. a hidden tab); the stylesheet's left/right span it.
  if (visibleWidthPx === 0) return undefined;

  const leftInsetPx = getLeftInsetPx();

  return {
    left: scrollLeftPx + leftInsetPx,
    width: Math.max(0, visibleWidthPx - leftInsetPx),
  };
}

/**
 * Where the banner's left edge sits, flush against the vertical ruler's right border. Read at
 * render time rather than module scope: `src/editor` is a barrel that imports this file back, so
 * the ruler's width is not guaranteed to be initialized while this module's body runs.
 */
function getLeftInsetPx() {
  return verticalRulerWidthPx + 1;
}
